import { supabase } from '../clients/supabaseClient';
import type { Picker } from '../types';

const PICKER_SELECT = `*, store:stores (id, name)`;

export async function fetchPickersFromDb(): Promise<Picker[]> {
    const { data, error } = await supabase
        .from('pickers')
        .select(PICKER_SELECT)
        .is('deleted_at', null)
        .order('first_name');

    if (error) throw error;
    return (data ?? []) as Picker[];
}

export type CreatePickerPayload = {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    cellphone: string;
    store_id: string;
};

export async function createPickerInDb(payload: CreatePickerPayload): Promise<Picker> {
    const { data: authData, error: authError } = await supabase.auth.signUp({
        email:    payload.email,
        password: payload.password,
    });

    if (authError) throw authError;
    if (!authData?.user) throw new Error('Auth signup returned no user');

    const { error: profileError } = await supabase
        .from('profiles')
        .insert({
            id:         authData.user.id,
            first_name: payload.first_name.trim(),
            last_name:  payload.last_name.trim(),
            cellphone:  payload.cellphone,
            is_admin:   false,
            role:       'picker',
        });

    if (profileError) throw profileError;

    const { data, error: pickerError } = await supabase
        .from('pickers')
        .insert([{
            first_name: payload.first_name.trim(),
            last_name:  payload.last_name.trim(),
            auth_id:    authData.user.id,
            store_id:   payload.store_id,
        }])
        .select(PICKER_SELECT)
        .single();

    if (pickerError) throw pickerError;
    return data as Picker;
}

export async function updatePickerPhoneInDb(
    authId: string | number,
    cellphone: string
): Promise<void> {
    const { error } = await supabase
        .from('profiles')
        .update({ cellphone })
        .eq('id', authId);

    if (error) throw error;
}

export async function fetchPickerPhoneFromDb(authId: string | number): Promise<string> {
    const { data } = await supabase
        .from('profiles')
        .select('cellphone')
        .eq('id', authId)
        .single();

    return data?.cellphone ?? '';
}

export async function deletePickerFromDb(pickerId: string): Promise<void> {
    const { error } = await supabase
        .from('pickers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', pickerId);

    if (error) throw error;
}
